import { Box } from "@material-ui/core";
import React from "react";
import { StringParam, useQueryParam } from "use-query-params";
import OilAromaIcon from "../../../components/oil-aroma-icon";
import OilEffectIcon from "../../../components/oil-efffect-icon";
import SeparatorBullet from "../../../components/separator-bullet";
import { getDefaultProductVariant } from "../../../helpers/product";
import useIsMounted from "../../../hooks/use-is-mounted";
import useTranslate from "../../../hooks/use-translate";
import { ProductType, ProductVariantType } from "../../../types/airgraft";

/**
 * Add dynamic content modules for oil products using Oil variant details information
 */
export const generateOilDynamicContentModules = (
  productVariant: ProductVariantType
): Queries.ProductDetailsPageDataQuery["datoCmsProduct"]["variants"][number]["modules"] => {
  const t = useTranslate();
  const modules = [];
  const { variantDetail } = productVariant;

  // Aroma & Flavor Chart
  if (variantDetail?.aromasDetails) {
    modules.push({
      model: {
        apiKey: "generated_c_module_pv_oil_aromasDetails"
      },
      title: t("pdp.aromasDetails.title"),
      chartData: variantDetail?.aromasDetails
    });
  }

  // Effects Chart
  if (variantDetail?.effectsDetails) {
    modules.push({
      model: {
        apiKey: "generated_c_module_pv_oil_effectsDetails"
      },
      title: t("pdp.effectsDetails.title"),
      chartData: variantDetail?.effectsDetails
    });
  }

  // Oil profile
  const oilProfileModule = {
    model: {
      apiKey: "c_module_pv_tech_spec"
    },
    title: t("pdp.oilProfile.title"),
    description: productVariant.descriptionNode.childMarkdownRemark.html,
    specs: []
  };
  if (variantDetail?.plantSpeciesDominance) {
    oilProfileModule.specs.push({
      title: t("pdp.oilProfile.dominance"),
      value: variantDetail?.plantSpeciesDominance
    });
  }
  if (variantDetail?.aromas.length > 0) {
    oilProfileModule.specs.push({
      title: t("pdp.oilProfile.tastingNotes"),
      value: (
        <Box display="flex" flexWrap="wrap">
          {variantDetail.aromas.map((a, i) => (
            <Box
              key={a.title}
              component="span"
              alignItems="center"
              display={{
                xs: i === 2 ? "none" : "flex",
                sm: "flex"
              }}
            >
              <OilAromaIcon aroma={a} />
              <span>{a.title}</span>
              {i !== variantDetail.aromas.length - 1 && (
                <SeparatorBullet color="yellow" />
              )}
            </Box>
          ))}
        </Box>
      )
    });
  }
  if (variantDetail?.effects.length > 0) {
    oilProfileModule.specs.push({
      title: t("pdp.oilProfile.effect"),
      value: (
        <Box display="flex" flexWrap="wrap">
          {variantDetail?.effects.map((e, i) => (
            <Box
              key={e.name}
              component="span"
              alignItems="center"
              display={{
                xs: i === 2 ? "none" : "flex",
                sm: "flex"
              }}
            >
              <OilEffectIcon effect={e} />
              <span>{e.name}</span>
              {i !== variantDetail.effects.length - 1 && (
                <SeparatorBullet color="yellow" />
              )}
            </Box>
          ))}
        </Box>
      )
    });
  }
  if (variantDetail?.intensity) {
    oilProfileModule.specs.push({
      title: t("pdp.oilProfile.intensity"),
      value: getOilIntensityLabel(variantDetail?.intensity)
    });
  }

  if (oilProfileModule.specs.length > 0) {
    modules.push(oilProfileModule);
  }

  // Oil contents
  if (
    variantDetail?.coaReport &&
    variantDetail?.coaReport.ingredients &&
    variantDetail?.coaReport.ingredients.length > 0
  ) {
    modules.push({
      model: {
        apiKey: "generated_c_module_pv_oil_coaReport_ingredients"
      },
      title: t("pdp.coaReportIngredients.title"),
      coaReport: variantDetail?.coaReport
    });
  }

  // Brand profile
  if (productVariant.brand) {
    modules.push({
      model: {
        apiKey: "generated_c_module_pv_brand_profile"
      },
      title: t("pdp.brandProfile.title"),
      brand: productVariant.brand
    });
  }

  // Certificate of Analysis
  if (variantDetail?.coaReport) {
    modules.push({
      model: {
        apiKey: "generated_c_module_pv_oil_coaReport"
      },
      title: t("pdp.coaReport.title"),
      coaReport: variantDetail?.coaReport
    });
  }

  return modules;
};

export const getOilIntensityLabel = (intensity: number) => {
  const t = useTranslate();
  switch (intensity) {
    case 3: {
      return t("pdp.oilProfile.intensity.potentPsychoactive");
    }
    case 2: {
      return t("pdp.oilProfile.intensity.potentPsychoactive");
    }
    case 1: {
      return t("pdp.oilProfile.intensity.mildPsychoactive");
    }
    case 0: {
      return t("pdp.oilProfile.intensity.nonPsychoactive");
    }
  }
};

export const getProductInventoryLevel = (availableQuantity: number) => {
  if (!availableQuantity) {
    return null;
  }
  if (availableQuantity <= 5) {
    return "LOW";
  }
  if (availableQuantity <= 15) {
    return "MEDIUM";
  }
  return "GOOD";
};

export const useProductVariantByQueryParam = (
  product: ProductType
): [ProductVariantType, (variandId: string) => void] => {
  const isMounted = useIsMounted();
  const [queryParamVariantId, setQueryParamVariantId] = useQueryParam(
    "variant",
    StringParam
  );
  if (isMounted && queryParamVariantId) {
    const variant = product.variants.find(
      v => v.originalId === queryParamVariantId
    );
    if (variant) {
      return [variant, setQueryParamVariantId];
    }
  }
  return [getDefaultProductVariant(product), setQueryParamVariantId];
};
